import { provide } from 'vue';
import type { TOptionsBase, i18n } from 'i18next';
import { useTranslation as useI18nextVueTranslation } from "i18next-vue";
import {
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord,
  injectRequiredWithSelf
} from "@mcwd/util-vue";
import { useFormLogger } from "./use-form-logger.js";
import type { PartialWithNil } from '@mcwd/typescript-type-guards';

export type TranslateOptions = PartialWithNil<TOptionsBase>;
export type TranslateFunction = (key: string, options?: TranslateOptions | string | undefined) => string;

type UseTranslationInjectionValues = InjectionValuesRecord<{
  i18next: i18n;
  translate: TranslateFunction;
}>;

type UseTranslationInjectionKeys = InjectionKeysRecord<UseTranslationInjectionValues>;

const UseTranslationInjectionKeys = CreateInjectionKeysRecord<UseTranslationInjectionKeys>({
  i18next: "i18next",
  translate: "translate"
});


export function initUseTranslation()
{
  if (checkValuesAlreadyProvided(UseTranslationInjectionKeys)) {
    return;
  }
  const {i18next, t: translate } = useI18nextVueTranslation();
  provide(UseTranslationInjectionKeys.i18next, i18next);
  provide(UseTranslationInjectionKeys.translate, (translate as TranslateFunction));
}


export function useTranslation ({ callerLabel }: { callerLabel?: string | undefined; } = {}): { i18next: i18n; translate: TranslateFunction; } {
  const { logger } = useFormLogger();
  try
  {
    const i18next = injectRequiredWithSelf(UseTranslationInjectionKeys.i18next);
    const translate = injectRequiredWithSelf(UseTranslationInjectionKeys.translate);
    return { i18next, translate };
  }
  catch (err)
  {
    logger.error(`InjectionError from ${ callerLabel ?? 'unknown' }`);
    throw err;
  }
}